import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { ListingBookingAdminInfoResultViewModel } from '@oa/shared-core';
import ListingBookingAdminInfoViewModel from '@app/models/listing-booking-admin-info-view-model';
import { ArrayService, DateService, LogService, UserService } from '@app/services';
import { PhoneNumberPipe } from '../pipes/phone-number.pipe';
import { LinkToBookingDetailComponent } from '../../common-components/link-to-booking-detail/link-to-booking-detail.component';
import { SortIndicatorComponent } from '../../common-components/sort-indicator/sort-indicator.component';
import { CurrencyPipe, DatePipe } from '@angular/common';

/**
 * A grid that shows Bookings w/ status and has various links to things like Reservation and Renter Email.
 *
 * Example: <booking-grid [bookings]="bookings" [displayNotes]="true" [sortEnabled]="true" > </booking-grid>
 *
 * Required Inputs: bookings - an array of ListingBookingAdminInfoResultViewModel
 *
 * Optional Inputs:
 *   1. displayNotes - Shows the abbreviation key below the grid. Default: true
 *   2. loading - Indicates that the component (or a bound component) is loading.
 *   3. sortEnabled - Will enable sorts on a subset of columns (StartDate, EndDate, Renter Email, Reservation ID)
 */
@Component({
    selector: "booking-grid",
    templateUrl: "./booking-grid.component.html",
    styleUrls: ["./booking-grid.component.less"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SortIndicatorComponent, LinkToBookingDetailComponent, CurrencyPipe, DatePipe, PhoneNumberPipe]
})
export class BookingGridComponent implements OnInit {
  private readonly _componentName = 'BookingGridComponent';
  private _bookings: ListingBookingAdminInfoResultViewModel[];
  private _displayNotes = true;
  private _showOrderInfoColumn = false;

  @Input()
  public sortEnabled = false;

  public sortCol = 'startDate';
  public sortDir = 'desc';

  public waiting: boolean;

  @Input()
  public loading: boolean;

  @Input()
  public showAmount: boolean = true;

  @Input()
  public showRenterContactInfo: boolean = true;

  constructor(
    private log: LogService,
    private dateHelper: DateService,
    private router: Router,
    private userService: UserService,
    private arrayHelper: ArrayService
  ) {}

  ngOnInit() {}

  /**
   * Is verbose logging enabled
   */
  private get verboseLogging(): boolean {
    return this.log.verboseLogging;
  }

  public get bookings(): ListingBookingAdminInfoResultViewModel[] {
    return this._bookings;
  }

  @Input()
  public set bookings(v: ListingBookingAdminInfoResultViewModel[]) {
    if (this.sortEnabled) {
      this.applySort(v);
    } else {
      this._bookings = v;

      if (this.verboseLogging) {
        console.log(`${this._componentName}.bookings set v=`, v);
      }
    }
  }

  public get isAdmin(): boolean {
    // console.log(`${this._componentName}.isAdmin get userService.isAdmin=`, this.userService.isAdmin);

    return this.userService.isAdmin;
  }

  public get displayNotes(): boolean {
    return this._displayNotes;
  }

  @Input()
  public set displayNotes(v: boolean) {
    this._displayNotes = v;
  }

  public get sortDescending(): boolean {
    if (this.sortDir && (this.sortDir.startsWith('d') || this.sortDir.startsWith('D'))) {
      return true;
    }

    return false;
  }

  public setSortCol(col: string) {
    if (col === this.sortCol) {
      // reverse the sort
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortCol = col;
      this.sortDir = 'asc';
    }

    // console.log(`${this._componentName}.setSortCol - sortCol=${this.sortCol},sortDir=${this.sortDir},sortDescending=${this.sortDescending}`);

    this.applySort(this.bookings);
  }

  private applySort(items: ListingBookingAdminInfoResultViewModel[]) {
    if (this.sortDir.toLowerCase() === 'desc') {
      this._bookings = this.arrayHelper.orderByDescending(items, this.sortCol);
    } else {
      this._bookings = this.arrayHelper.orderBy(items, this.sortCol);
    }
  }

  public isToday(model: ListingBookingAdminInfoViewModel): boolean {
    if (!model) {
      return false;
    }

    const startDate = this.dateHelper.makeValid(model.startDate);

    return this.dateHelper.isToday(startDate);
  }

  public isUpcoming(model: ListingBookingAdminInfoViewModel): boolean {
    if (!model) {
      return false;
    }

    const startDate = this.dateHelper.makeValid(model.startDate);

    return !model.canceled && this.dateHelper.isDateGreaterThan(startDate, new Date());
  }

  public isPast(model: ListingBookingAdminInfoViewModel): boolean {
    if (!model) {
      return false;
    }

    const startDate = this.dateHelper.makeValid(model.startDate);

    return !model.canceled && this.dateHelper.isDateLessThan(startDate, new Date());
  }

  public get showOrderInfoColumn(): boolean {
    return this._showOrderInfoColumn;
  }
  public set showOrderInfoColumn(v: boolean) {
    this._showOrderInfoColumn = v;
  }

  /**
   * onRenterEmailAddressClick(item)
   */
  public onRenterEmailAddressClick(item: ListingBookingAdminInfoViewModel) {
    console.log(`${this._componentName}.onRenterEmailAddressClick() item=`, item);
    this.router.navigate(['/admin/account', item.renterUserAccountId]);
  }

  /**
   * Handles the click event when the reservation ID is clicked
   * @param item
   */
  public onReservationClick(item: ListingBookingAdminInfoViewModel) {
    console.log(`${this._componentName}.onReservationClick() item=`, item);

    if (item.reservationId) {
      this.router.navigate(['/admin/reservation', item.reservationId]);
    }
  }

  /**
   * Creates a full name string from first/last name of the ListingBookingAdminInfoViewModel
   */
  public getFullName(item: ListingBookingAdminInfoViewModel) {
    if (!item) {
      return null;
    }

    if (!(item.firstName && item.lastName)) {
      return null;
    }

    let name = `${item.firstName} ${item.lastName}`;

    return name;
  }
}
